import { WsServerEvent } from "@vb/rgs-contract";

/**
 * Snapshot dispatched to the GameView resync listener whenever a fresh socket
 * (re)authenticates. Mirrors the server's `round:resync` event but is also used
 * to surface terminal connection failures (max retries hit) so the UI can
 * prompt the player to refresh.
 */
export interface WsResyncEvent {
  /** "idle" if no active round, "live" if mid-round, "resolved" if just
   *  finished. `error` is set to a human-readable string when reconnect has
   *  given up — UI should treat it as a terminal state. */
  phase: "idle" | "live" | "resolved" | "error";
  roundId?: string;
  startedAt?: number;
  elapsedMs?: number;
  currentMultiplier?: number;
  serverSeedHash?: string;
  error?: string;
}

export interface WsOptions {
  url: string;
  token: string;
  onEvent: (event: WsServerEvent) => void;
  onResync?: (snapshot: WsResyncEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 5000;
const MAX_JITTER_MS = 500;
const MAX_RETRIES = 10;

/**
 * Derive a WebSocket URL from window.location + an optional API host. The
 * protocol is forced to ws/wss to match the page's http/https — mixing them
 * triggers a hard browser error in production.
 *
 * - apiHost set ("vaultbreaker-api.fly.dev" or "wss://...") → use that host.
 * - apiHost empty/undefined → same-origin (Vite proxy handles in dev).
 */
export function deriveWsUrl(apiHost: string | undefined, path = "/game/stream"): string {
  const wsProto = typeof window !== "undefined" && window.location.protocol === "https:"
    ? "wss"
    : "ws";
  if (!apiHost) {
    const host = typeof window !== "undefined" ? window.location.host : "localhost";
    return `${wsProto}://${host}${path}`;
  }
  // Strip any leading scheme the operator may have included (https://, wss://)
  // so we always pin the protocol to the page's http/https. Avoids a footgun
  // where VITE_WS_BASE=https://… would build an https://…/game/stream URL.
  const stripped = apiHost.replace(/^[a-z]+:\/\//i, "").replace(/\/$/, "");
  return `${wsProto}://${stripped}${path}`;
}

export class WsClient {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private stopped = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private opts: WsOptions) {}

  connect(): void {
    this.stopped = false;
    const ws = new WebSocket(this.opts.url);
    this.ws = ws;

    ws.addEventListener("open", () => {
      this.attempt = 0;
      ws.send(JSON.stringify({ type: "auth", token: this.opts.token }));
      this.opts.onOpen?.();
    });

    ws.addEventListener("message", (ev) => {
      let raw: unknown;
      try {
        raw = JSON.parse(String(ev.data));
      } catch {
        return; // malformed JSON — drop
      }
      // round:resync is the catch-up snapshot the server fires post-auth.
      // Routed to the resync listener (NOT the regular event handler) so the
      // store reducer doesn't need a new case — the listener dispatches the
      // existing setters directly.
      if (
        raw &&
        typeof raw === "object" &&
        (raw as { type?: unknown }).type === "round:resync"
      ) {
        const r = raw as Partial<WsResyncEvent> & { phase?: WsResyncEvent["phase"] };
        if (r.phase === "idle" || r.phase === "live" || r.phase === "resolved") {
          this.opts.onResync?.({
            phase: r.phase,
            roundId: r.roundId,
            startedAt: r.startedAt,
            elapsedMs: r.elapsedMs,
            currentMultiplier: r.currentMultiplier,
            serverSeedHash: r.serverSeedHash,
          });
        }
        return;
      }
      const parsed = WsServerEvent.safeParse(raw);
      if (parsed.success) this.opts.onEvent(parsed.data);
    });

    ws.addEventListener("close", () => {
      this.opts.onClose?.();
      if (this.stopped) return;
      if (this.attempt >= MAX_RETRIES) {
        // Give up. Surface a terminal error to the resync listener so the UI
        // can prompt a refresh instead of silently spinning.
        this.opts.onResync?.({
          phase: "error",
          error: "Lost connection to game server. Please refresh.",
        });
        return;
      }
      // Exponential backoff with jitter — base * 2^attempt + random(0..500ms),
      // capped at 5s. The jitter prevents N reconnecting clients from
      // hammering the server in lock-step after a brief outage.
      const exp = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** this.attempt);
      const jitter = Math.random() * MAX_JITTER_MS;
      const delay = Math.min(MAX_DELAY_MS, exp + jitter);
      this.attempt += 1;
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect();
      }, delay);
    });

    ws.addEventListener("error", () => {
      // close will follow and trigger reconnect
    });
  }

  send(payload: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  close(): void {
    this.stopped = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
  }
}
